import { SmileOutlined } from '@ant-design/icons'
import { useTheme } from '@app/providers/theme-provider'
import Picker from '@emoji-mart/react'
import { classNames } from '@shared/lib/classNames'
import { ReactQuill } from '@shared/lib/quill'
import { Button, Input, Modal, Popover, Tooltip } from 'antd'
import { useEffect, useRef, useState } from 'react'
import type { DeltaStatic, EmitterSource } from 'react-quill-new'
import 'react-quill-new/dist/quill.bubble.css'
import './text-editor.scss'

interface TextEditorProps {
    value?: string
    viewToken?: string
    maxLength?: number
    onChange?: (value: string) => void
}

const modules = {
    toolbar: {
        container: '#toolbar'
    }
}
const initialRange = {
    index: 0,
    length: 0
}

export const TextEditor = ({ value, viewToken, maxLength = 1024, onChange }: TextEditorProps) => {
    const [isEmojiPickerVisible, setIsEmojiPickerVisible] = useState(false)
    const [open, setOpen] = useState(false)
    const [linkValue, setLinkValue] = useState('')
    const [range, setRange] = useState(initialRange)
    const quillRef = useRef<ReactQuill>(null)

    useEffect(() => {
        quillRef.current?.focus()
    }, [])

    useEffect(() => {
        const quill = quillRef.current?.getEditor()

        if (quill) {
            quill.on('selection-change', (range) => {
                if (range) {
                    cursorPositionRef.current = range.index
                }
            })
        }

        return () => {
            if (quill) {
                quill.off('selection-change')
            }
        }
    }, [])

    const cursorPositionRef = useRef(0)

    const { theme } = useTheme()

    const [charCount, setCharCount] = useState(0)

    const handleChange = (
        value: string,
        delta: DeltaStatic,
        source: EmitterSource,
        editor: ReactQuill.UnprivilegedEditor
    ) => {
        cursorPositionRef.current = editor.getSelection()?.index || 0
        onChange?.(value)

        setCharCount(editor.getText().trim().length)
    }

    const handleToolbarLink = () => {
        const quill = quillRef.current?.getEditor()
        quill?.focus()
        const range = quill?.getSelection()
        if (range) {
            setRange(range)
            setOpen(true)
        } else {
            Modal.warning({
                title: 'Выделите текст',
                content: 'Пожалуйста, выделите текст, чтобы добавить ссылку.'
            })
        }
    }

    const handleOk = () => {
        const quill = quillRef.current?.getEditor()
        if (linkValue && quill) {
            quill.formatText(
                range.index,
                range.length,
                'link',
                `${window.location.origin}/redirect?view_token=${viewToken}`
            )
        }
        if (linkValue && quill) {
            quill.formatText(range.index, range.length, 'link', linkValue)
        }
        setOpen(false)
        setLinkValue('')
        setRange(initialRange)
    }

    const handleCancel = () => {
        setOpen(false)
        setLinkValue('')
        setRange(initialRange)
    }

    return (
        <>
            <div className="editor-container">
                <Popover
                    content={
                        <Picker
                            locale="ru"
                            theme={theme}
                            onEmojiSelect={(emoji) => {
                                const quill = quillRef.current?.getEditor()
                                if (quill) {
                                    const cursorPosition = cursorPositionRef.current
                                    quill.insertText(cursorPosition, emoji.native)
                                    quill.setSelection(cursorPosition + emoji.native.length)
                                }
                                setIsEmojiPickerVisible(false)
                            }}
                            showPreview={false}
                            skinTonePosition={'none'}
                            previewPosition={'none'}
                            set={'apple'}
                        />
                    }
                    trigger={'click'}
                    open={isEmojiPickerVisible}
                    placement="bottomRight"
                >
                    <Button
                        type="text"
                        icon={<SmileOutlined />}
                        onClick={() => setIsEmojiPickerVisible(true)}
                        className="emoji-btn"
                    />
                </Popover>
                <div id="toolbar">
                    <Tooltip title="Жирный текст" placement="top">
                        <button className="ql-bold"></button>
                    </Tooltip>
                    <Tooltip title="Курсив" placement="top">
                        <button className="ql-italic"></button>
                    </Tooltip>
                    <Tooltip title="Подчеркнутый текст" placement="top">
                        <button className="ql-underline"></button>
                    </Tooltip>
                    <Tooltip title="Зачеркнутый текст" placement="top">
                        <button className="ql-strike"></button>
                    </Tooltip>
                    <Tooltip title="Код" placement="top">
                        <button className="ql-code"></button>
                    </Tooltip>
                    <Tooltip title="Спойлер" placement="top">
                        <button className="ql-spoiler"></button>
                    </Tooltip>
                    <Tooltip title="Ссылка" placement="top">
                        <button className="ql-link" onClick={handleToolbarLink}></button>
                    </Tooltip>
                    <Tooltip title="Кодовый блок" placement="top">
                        <button className="ql-code-block"></button>
                    </Tooltip>
                    <Tooltip title="Цитата" placement="top">
                        <button className="ql-blockquote"></button>
                    </Tooltip>
                </div>
                <ReactQuill
                    theme="bubble"
                    value={value}
                    onChange={handleChange}
                    modules={modules}
                    placeholder="Введите текст..."
                    ref={quillRef}
                    className={classNames('editor', {}, [theme])}
                />

                <div className="char-count">
                    {charCount}/{maxLength}
                </div>

                <Modal
                    title="Добавить ссылку"
                    open={open}
                    onOk={handleOk}
                    onCancel={handleCancel}
                    okText="Добавить"
                    cancelText="Отмена"
                >
                    <Input
                        placeholder="Введите URL"
                        value={linkValue}
                        onChange={(e) => setLinkValue(e.target.value)}
                        style={{ marginBottom: 16 }}
                    />
                </Modal>
            </div>
        </>
    )
}
